import React from 'react';
import { WifiOff, Wifi } from 'lucide-react';
import { useConnectivityStatus } from '../../hooks/useConnectivityStatus';

/**
 * MobileConnectivityBanner — lightweight sticky strip for the technician
 * mobile pages. Hidden while online; visible when offline (red) or in the
 * ~4-second "Back online" reconnect flash (green).
 *
 * Positioned to slot BELOW the existing sticky page header. Purely
 * presentational; the underlying state comes from `useConnectivityStatus`.
 * Never covers the primary content area on 375-px viewports because it
 * only occupies a single 32-px row.
 */
export const MobileConnectivityBanner = ({ className = '' }) => {
  const { status } = useConnectivityStatus();
  if (status === 'online') return null;

  const cfg = status === 'offline'
    ? {
        bg: 'bg-red-600',
        text: 'text-white',
        icon: WifiOff,
        label: 'No connection — job completion may fail. Reconnect to sync.',
        testId: 'mobile-connectivity-status-offline',
      }
    : {
        bg: 'bg-emerald-600',
        text: 'text-white',
        icon: Wifi,
        label: 'Back online.',
        testId: 'mobile-connectivity-status-reconnecting',
      };

  const Icon = cfg.icon;
  return (
    <div
      role="status"
      aria-live="polite"
      data-testid="mobile-connectivity-banner"
      className={`w-full px-4 py-2 flex items-center gap-2 text-xs font-semibold ${cfg.bg} ${cfg.text} ${className}`}
    >
      <span data-testid={cfg.testId} className="inline-flex items-center gap-2">
        <Icon className="h-4 w-4 shrink-0" />
        <span className="leading-tight">{cfg.label}</span>
      </span>
    </div>
  );
};

export default MobileConnectivityBanner;
